import { useMemo, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { PageContainer } from '@/components/common/PageContainer'
import { PageEmpty } from '@/components/common/PageStates'
import { RfidAppHeader } from '@/components/rfid/RfidAppHeader'
import { BRAND } from '@/constants/brand'
import { getMovement, type MovementId } from '@/config/movements'
import { useAuthStore } from '@/stores/authStore'
import {
  transferCustomersFromUser,
  type TransferCustomerRow,
} from '@/utils/transferCustomers'
import { movementFlowLabel } from '@/utils/movementFlow'

function isTransferMovement(id: string | null): id is 'transfer_out' | 'transfer_in' {
  return id === 'transfer_out' || id === 'transfer_in'
}

export default function TransferCustomerPage() {
  const [params] = useSearchParams()
  const movementParam = params.get('movement')
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const [search, setSearch] = useState('')

  const valid = isTransferMovement(movementParam)
  const movement = valid ? getMovement(movementParam as MovementId) : null
  const rows = useMemo(() => transferCustomersFromUser(user), [user])

  const filtered = useMemo(() => {
    if (!search.trim()) return rows
    const q = search.trim().toLowerCase()
    return rows.filter(
      (row) =>
        row.customerName.toLowerCase().includes(q) || row.bpCode.toLowerCase().includes(q)
    )
  }, [rows, search])

  if (!valid || !movement) {
    return <Navigate to="/" replace />
  }

  const onSelect = (row: TransferCustomerRow) => {
    if (movement.id === 'transfer_in') {
      navigate(
        `/transfer/docs?movement=${movement.id}&bpCode=${encodeURIComponent(row.bpCode)}&customerName=${encodeURIComponent(row.customerName)}`
      )
      return
    }
    navigate(
      `/transfer/to-customer?movement=${movement.id}&fromBpCode=${encodeURIComponent(row.bpCode)}&fromCustomerName=${encodeURIComponent(row.customerName)}`
    )
  }

  return (
    <PageContainer fluid className="!px-0 !py-0" stack={false}>
      <div className="rfid-web-frame">
        <RfidAppHeader
          title={movement.title}
          subtitle={`${movementFlowLabel(movement)} · Select from-customer`}
          onBack={() => navigate('/')}
        />

        <div className="rfid-wizard-body overflow-y-auto">
          {rows.length === 0 ? (
            <PageEmpty description="No customers on your profile. Check IAM companies assignment." />
          ) : (
            <>
              <input
                type="search"
                className="rfid-search"
                placeholder="Search by name or BP code…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search customers"
              />

              {filtered.length === 0 ? (
                <PageEmpty description="No customers match your search." />
              ) : (
                <div className="rfid-doc-list">
                {filtered.map((row) => (
                  <button
                    key={row.bpCode}
                    type="button"
                    onClick={() => onSelect(row)}
                    className="rfid-doc-row"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block text-[14px] font-extrabold text-foreground">
                        {row.customerName}
                      </span>
                      <span className="mt-1 block text-[11px] font-semibold text-muted-foreground">
                        {row.bpCode}
                        {row.lineOfBusiness ? ` · ${row.lineOfBusiness}` : ''}
                      </span>
                    </span>
                    <ChevronRight size={16} color={BRAND} className="shrink-0" />
                  </button>
                ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </PageContainer>
  )
}
